import {
  fetchClient,
  queryClient,
  setOnUnauthenticated,
} from "@/core/api-client";
import {
  GUEST_MENU_PATHS,
  GUEST_PRODUCTS_LIST_KEY,
} from "@/core/menu/constants";
import { ORDER_PATHS } from "@/core/orders/constants";

/**
 * Подъём анонимной сессии.
 *
 * Раньше анонимный вход брался лениво внутри order-flow — тот заморожен до
 * Ф6, а REST требует cookie глобально. Теперь сессия поднимается на загрузке.
 *
 * Три защиты, и каждая закрывает реальный сценарий:
 *
 * 1. single-flight — параллельные 401 не поднимают несколько сессий подряд;
 * 2. потолок ПОДРЯД ИДУЩИХ неудач — `fetchClient.POST` НЕ бросает на
 *    HTTP-ошибку (обёртка возвращает {data, error}), а
 *    `unauthenticatedNotified` в транспорте сбрасывается ЛЮБЫМ ответом < 400,
 *    включая успешный анонимный вход. Если cookie не прилипла — а это штатная
 *    среда iframe-виджета при блокировке сторонних cookie — связка
 *    401 → POST 200 → инвалидация → 401 крутилась бы неограниченно.
 *    Считаем именно подряд идущие неудачи: удачный вход обнуляет счётчик,
 *    иначе долгоживущая вкладка исчерпала бы потолок на трёх законных
 *    протуханиях сессии и перестала бы её поднимать навсегда;
 * 3. инвалидация /me, гостевых путей меню и путей заказа. До Ф4б гасили
 *    ТОЛЬКО /me:
 *    будить обсерверы замороженных доменов было незачем. Ф4б их
 *    разморозила, и точечная инвалидация стала дефектом — цепочка рвалась
 *    молча: гостевой запрос упал 401 → сессия поднялась → упавший запрос
 *    никто не перезапросил (queryClient стоит с retry: false) →
 *    reportError на 401 молчит → гость видит пустое меню без единого
 *    признака ошибки. Ф7 добавила в список заказы: поднятая заново
 *    анонимная сессия — это НОВЫЙ пользователь, и выборка scope=own у него
 *    другая. Оставь заказы в кэше — и гость увидел бы чужой черновик.
 */
const MAX_ATTEMPTS = 3;

let inFlight: Promise<boolean> | null = null;
let failures = 0;

export function ensureAnonymousSession(): Promise<boolean> {
  if (inFlight) return inFlight;
  if (failures >= MAX_ATTEMPTS) return Promise.resolve(false);

  inFlight = fetchClient
    .POST("/session/anonymous")
    .then(({ error }) => {
      if (error) {
        failures += 1;
        return false;
      }
      failures = 0;
      void queryClient.invalidateQueries({ queryKey: ["get", "/me"] });
      for (const path of [...GUEST_MENU_PATHS, ...ORDER_PATHS]) {
        void queryClient.invalidateQueries({ queryKey: ["get", path] });
      }
      // Обход товаров живёт под собственным ключом, и цикл по путям выше
      // его не накрывает. Без этой строки повторяется ровно тот дефект,
      // ради которого инвалидация здесь и появилась: гостевые запросы
      // падают 401, сессия поднимается, все оживают — кроме товаров, и
      // гость видит меню без единой позиции.
      void queryClient.invalidateQueries({ queryKey: GUEST_PRODUCTS_LIST_KEY });
      return true;
    })
    .catch(() => {
      // Отказ транспорта (сеть легла, запрос отменён) — такая же неудача,
      // как и HTTP-ошибка: без учёта потолок бы её не ловил.
      failures += 1;
      return false;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Для тестов и ручной проверки: обнулить счётчик неудач. */
export function resetAnonymousSessionAttempts(): void {
  failures = 0;
}

/**
 * 401 в виджете — не «разлогинили», а «сессии ещё нет»: гостю неоткуда её
 * взять, кроме как выписать себе анонимную. Экрана входа здесь нет.
 *
 * Транспорт повтор упавшего запроса не делает: onUnauthenticated в core —
 * синхронный fire-and-forget колбэк. Повтор обеспечивает инвалидация /me.
 */
setOnUnauthenticated(() => {
  void ensureAnonymousSession();
});
